import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, MessageCircle, Users2, TrendingUp, ShoppingBasket, ChartLine, Flame, Check, Star, Building2, ClipboardList, ShieldCheck, PackageOpen, UserPlus, Search, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { BetaBadge } from '@/components/BetaBadge';
import { InstallAppCTA, InstallAppSection } from '@/components/InstallAppCTA';
import sisthokoLogo from '@/assets/sisthoko-logo.webp';
import { SEO } from '@/components/SEO';

const benefits = [
  { icon: Sparkles, title: 'Save 15–20% Every Order', desc: 'Sis Thoko scans Makro, Jumbo, PnP Wholesale, Shoprite and 50+ suppliers daily to find the cheapest prices on YOUR ingredients.' },
  { icon: MessageCircle, title: 'Personalised WhatsApp Deals', desc: 'Every week, a personalised sourcing plan goes straight to your WhatsApp. No app to download. No website to log in to.' },
  { icon: ShoppingBasket, title: 'A Basket Built for You', desc: 'Baker, caterer, or food store? Sis Thoko learns exactly what you use and only shows you what is relevant.' },
  { icon: Users2, title: 'Group Buying Power', desc: 'Join buying pools with other foodpreneurs in your community to unlock bulk wholesale prices alone you could never reach.' },
  { icon: ChartLine, title: 'Grow Your Profit Margins', desc: 'Lower input costs mean higher profit per cake, per dish, per event — every single month.' },
  { icon: TrendingUp, title: 'Monthly Savings Report', desc: 'A simple report showing what you spent, where you saved, which suppliers gave the best value, and projected next-month savings.' },
];

const audiences = [
  { emoji: '🧁', title: 'Bakers', desc: 'Cakes, breads, pastries, vetkoek, magwinya' },
  { emoji: '🍲', title: 'Caterers', desc: 'Events, funerals, parties, graduations, corporates' },
  { emoji: '🏪', title: 'Food Stores', desc: 'Spaza, restaurants, takeaways' },
];

const testimonials = [
  { name: 'Nomvula M.', role: 'Baker, Soweto', quote: 'Sis Thoko found me cake flour 22% cheaper than my regular store. That is real money in my pocket every single month.' },
  { name: 'Sipho K.', role: 'Caterer, Tembisa', quote: 'I cater 4 funerals a month. Sis Thoko sends me my shopping list on WhatsApp every Sunday — I just buy.' },
  { name: 'Lerato D.', role: 'Spaza Owner, Mamelodi', quote: 'Joined a group buy through Sis Thoko and got rice at wholesale price. Saved over R600 in one order.' },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Sis Thoko — AI Sourcing for Township Foodpreneurs"
        description="AI sourcing companion for South African foodpreneurs — best wholesale prices across 50+ suppliers, saving 15–20% monthly via WhatsApp."
        path="/"
      />
      {/* Beta banner */}
      <div className="bg-primary/5 border-b border-primary/10 text-center text-xs text-foreground/80 py-1.5 px-4">
        <span className="inline-flex items-center gap-2">
          <BetaBadge variant="default" />
          Sis Thoko is currently in beta — features and pricing may change as we improve.
        </span>
      </div>
      {/* Nav */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="container mx-auto flex items-center justify-between h-16 px-4">
          <div className="flex items-center gap-2">
            <img src={sisthokoLogo} alt="Sis Thoko" className="h-14 md:h-16 object-contain" loading="eager" width={512} height={303} style={{ width: 'auto' }} />
            <BetaBadge variant="subtle" />
          </div>
          <nav className="hidden md:flex items-center gap-7 text-sm text-muted-foreground">
            <a href="#benefits" className="hover:text-foreground transition-colors">Benefits</a>
            <a href="#how-it-works" className="hover:text-foreground transition-colors">How It Works</a>
            <Link to="/help" className="hover:text-foreground transition-colors">Help</Link>
            <Link to="/login">
              <Button size="sm" variant="outline" className="rounded-full px-5">Log In</Button>
            </Link>
            <Link to="/signup">
              <Button size="sm" className="rounded-full gradient-primary text-primary-foreground shadow-md hover:shadow-lg transition-shadow px-5">
                Get Started Free
              </Button>
            </Link>
          </nav>
          <div className="flex md:hidden items-center gap-2">
            <Link to="/login">
              <Button size="sm" variant="outline" className="rounded-full px-4">Log In</Button>
            </Link>
            <Link to="/signup">
              <Button size="sm" className="rounded-full gradient-primary text-primary-foreground px-4">Join Free</Button>
            </Link>
          </div>
        </div>
      </header>

      <main>
      {/* Hero */}
      <section className="relative overflow-hidden bg-mesh">
        <div className="container mx-auto px-4 pt-8 pb-12 md:pt-12 md:pb-20 relative">
          <div className="rounded-3xl gradient-sapphire p-6 md:p-10 lg:p-14 text-white shadow-elevated relative overflow-hidden">
            <div className="absolute inset-0 bg-mesh opacity-20" />
            <div className="relative grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight text-balance">
                  Smart AI Sourcing for{' '}
                  <span className="bg-gradient-to-r from-white to-white/70 bg-clip-text text-transparent">
                    Kasipreneur Foodpreneurs
                  </span>
                </h1>
                <p className="mt-6 text-lg text-white/80 max-w-xl leading-relaxed">
                  Meet Sis Thoko — your AI-powered sourcing companion. She hunts the cheapest prices across 50+ suppliers daily and sends them to your WhatsApp. Save 15–20% every month.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row items-start gap-3">
                  <Link to="/signup">
                    <Button size="lg" className="rounded-full bg-white text-primary hover:bg-white/90 text-base px-8 shadow-lg hover:shadow-xl transition-shadow">
                      Activate Sis Thoko Free <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
                <p className="mt-4 text-sm text-white/70">No credit card · Free to start · Built for South Africa</p>

                <div className="mt-10 flex items-center gap-8">
                  {[
                    { value: '50+', label: 'Suppliers scanned daily' },
                    { value: '15–20%', label: 'Average monthly savings' },
                    { value: 'WhatsApp', label: 'No app to download' },
                  ].map((s) => (
                    <div key={s.label}>
                      <p className="text-2xl md:text-3xl font-bold">{s.value}</p>
                      <p className="text-xs text-white/70">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-5">
                {/* Mock dashboard preview */}
                <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-5 shadow-elevated border border-white/10">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-xs text-white/70">This week's basket · WhatsApp ready</p>
                    <span className="text-[10px] font-semibold text-emerald-300 bg-emerald-400/20 px-2 py-0.5 rounded-full">LIVE</span>
                  </div>
                  <div className="space-y-2.5">
                    {[
                      { item: 'Cake Flour 12.5kg', store: 'Makro', price: 'R 189', save: 'R 32' },
                      { item: 'Sunflower Oil 5L', store: 'Jumbo', price: 'R 159', save: 'R 24' },
                      { item: 'Eggs 30 tray', store: 'PnP Wholesale', price: 'R 89', save: 'R 11', urgent: true },
                      { item: 'Sugar 10kg', store: 'Shoprite', price: 'R 209', save: 'R 18' },
                    ].map((d) => (
                      <div key={d.item} className="flex items-center justify-between p-3 rounded-2xl bg-white/10 border border-white/10">
                        <div className="flex items-center gap-2 min-w-0">
                          {d.urgent && <Flame className="h-3.5 w-3.5 text-amber-300 shrink-0" />}
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-white truncate">{d.item}</p>
                            <p className="text-[11px] text-white/60">Cheapest at {d.store}</p>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-bold text-emerald-300">{d.price}</p>
                          <p className="text-[10px] text-emerald-300/80">save {d.save}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 p-3 rounded-2xl bg-white/10 text-white flex items-center gap-2 border border-white/10">
                    <MessageCircle className="h-4 w-4 shrink-0" />
                    <p className="text-xs">Sent to your WhatsApp every Sunday at 6pm.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Audiences */}
      <section className="py-12 border-y border-border bg-muted/30">
        <div className="container mx-auto px-4">
          <p className="text-center text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-6">Built for every kind of foodpreneur</p>
          <div className="grid grid-cols-3 gap-4 max-w-3xl mx-auto">
            {audiences.map((a) => (
              <div key={a.title} className="text-center p-4 rounded-3xl glass-card hover:shadow-elevated transition-shadow">
                <div className="text-3xl mb-2">{a.emoji}</div>
                <p className="text-sm font-semibold text-foreground">{a.title}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section id="benefits" className="py-20 md:py-28 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-primary uppercase tracking-wider">Why Sis Thoko</span>
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mt-3 mb-4 text-balance tracking-tight">
              Every rand saved is profit earned
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Sis Thoko is more than an app — she is an AI-powered sourcing companion working for YOU every single day.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((b) => (
              <Card key={b.title} className="rounded-3xl border-border/50 hover:shadow-elevated transition-all group">
                <CardContent className="pt-6 pb-6">
                  <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <b.icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2 text-lg">{b.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-20 px-4 bg-muted/30">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-primary uppercase tracking-wider">How it works</span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-3 tracking-tight">Three steps. 24 hours to your first deals.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { step: '01', icon: UserPlus, title: 'Tell Sis Thoko about you', desc: 'A short questionnaire — your business type, your basket, your suppliers, your WhatsApp number.' },
              { step: '02', icon: Search, title: 'She hunts daily', desc: 'Sis Thoko scans Makro, Jumbo, PnP Wholesale, Shoprite and 50+ suppliers for the items YOU buy.' },
              { step: '03', icon: Phone, title: 'You receive deals on WhatsApp', desc: 'A personalised sourcing plan with exactly what to buy, where, and how much to spend.' },
            ].map((item) => (
              <div key={item.step} className="text-center p-6 rounded-3xl bg-background border border-border/50 hover:shadow-elevated transition-shadow">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full gradient-primary text-primary-foreground text-xl font-bold mb-6 shadow-elevated">
                  <item.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Suppliers */}
      <section className="py-20 px-4 bg-background">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="text-sm font-semibold text-primary uppercase tracking-wider">For Suppliers</span>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-3 mb-4 text-balance tracking-tight">
                Reach 1,000+ foodpreneurs through Sis Thoko
              </h2>
              <p className="text-muted-foreground leading-relaxed max-w-lg mb-8">
                Join our verified supplier network and put your products in front of township foodpreneurs actively looking for better prices. Our self-onboarding application gets you listed and ready to receive orders quickly.
              </p>
              <div className="flex flex-col sm:flex-row items-start gap-3">
                <Link to="/suppliers/apply">
                  <Button size="lg" className="rounded-full gradient-primary text-primary-foreground text-base px-8 shadow-glow hover:shadow-elevated transition-shadow">
                    Apply as a Supplier <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <p className="text-sm text-muted-foreground self-center">No setup fees · Reviewed within 48 hours</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: ClipboardList, title: 'Self-onboard in minutes', desc: 'Complete a simple, guided application. Save your progress and submit when you are ready.' },
                { icon: ShieldCheck, title: 'Verified supplier badge', desc: 'Build trust with buyers once your business and product details are reviewed.' },
                { icon: Users2, title: 'Direct access to buyers', desc: 'Connect with spazas, caterers, bakers and food stores looking for reliable supply.' },
                { icon: PackageOpen, title: 'Simple order management', desc: 'Receive orders, track demand and manage your catalogue from one place.' },
              ].map((item) => (
                <Card key={item.title} className="rounded-3xl border-border/50 hover:shadow-elevated transition-all group">
                  <CardContent className="pt-5 pb-5">
                    <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-3 group-hover:bg-primary/20 transition-colors">
                      <item.icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-1 text-lg">{item.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <span className="text-sm font-semibold text-primary uppercase tracking-wider">Foodpreneur stories</span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-3 tracking-tight">Foodpreneurs already winning</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {testimonials.map((t) => (
              <Card key={t.name} className="rounded-3xl border-border/50 hover:shadow-elevated transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-warning text-warning" />)}
                  </div>
                  <p className="text-sm text-foreground leading-relaxed mb-4">"{t.quote}"</p>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <InstallAppSection />
      <InstallAppCTA />

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-3xl">
          <div className="rounded-3xl gradient-sapphire p-10 md:p-16 text-center shadow-elevated relative overflow-hidden">
            <div className="absolute inset-0 bg-mesh opacity-30" />
            <div className="relative">
              <p className="text-white/80 italic text-sm mb-3">"Every rand saved is profit earned, Sisi."</p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 text-balance">
                Register today. Sis Thoko works for you within 24 hours.
              </h2>
              <p className="text-white/80 mb-8 max-w-lg mx-auto">
                Get started — completely free. No credit card. No app. Deals delivered to your WhatsApp.
              </p>
              <Link to="/signup">
                <Button size="lg" variant="secondary" className="rounded-full text-base px-10 shadow-lg">
                  Activate Sis Thoko Free <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <div className="mt-6 flex items-center justify-center gap-5 text-xs text-white/70">
                <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5" /> Free to start</span>
                <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5" /> WhatsApp delivery</span>
                <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5" /> Cancel anytime</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-10">
        <div className="container mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src={sisthokoLogo} alt="Sis Thoko" className="h-12 object-contain" loading="lazy" width={512} height={303} style={{ width: 'auto' }} />
          </div>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            
            <a href="#benefits" className="hover:text-foreground transition-colors">Benefits</a>
            <Link to="/login" className="hover:text-foreground transition-colors">Log In</Link>
          </div>
          <div className="flex flex-col items-center md:items-end gap-1">
            <p className="text-xs text-muted-foreground">Powered by © 2026 Imaginary Solutions Global (Pty) Ltd</p>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <Link to="/privacy" className="hover:text-foreground transition-colors hover:underline underline-offset-2">Privacy Policy</Link>
              <span>·</span>
              <Link to="/terms" className="hover:text-foreground transition-colors hover:underline underline-offset-2">Terms of Service</Link>
              <span>·</span>
              <Link to="/help" className="hover:text-foreground transition-colors hover:underline underline-offset-2">Help</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
